/* eslint-disable no-restricted-syntax */
import i18n from '@i18next/i18n';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { ConvertErrorType, DesktopErrorCode, ExporterErrorCode, ImporterErrorCode, MobileErrorCode } from '@tencent/mega-sdk-protocols';
import { DataConvertError } from '../interfaces/local-edit-data-converter.interface';

export interface ErrorNotice {
    category: E;
    description: string;
    action: ErrorNoticeAction;
    error: DataConvertError;
}

export function LoadErrorNotice(error: DataConvertError): ErrorNotice {
    const category = findErrorCategory(LOAD_ERROR_CATEGORY_MAP, error.code) ?? E.LOAD_FAILED;
    const description = ERROR_MESSAGE_TABLE[category];
    const action = LOAD_ERROR_ACTION_TABLE[category] ?? LOAD_ERROR_ACTION_TABLE[E.LOAD_FAILED];
    return { category, description, action, error };
}

export function SaveErrorNotice(error: DataConvertError): ErrorNotice {
    const category = findErrorCategory(SAVE_ERROR_CATEGORY_MAP, error.code) ?? E.SAVE_FAILED;
    const description = ERROR_MESSAGE_TABLE[category];
    const action = SAVE_ERROR_ACTION_TABLE[category] ?? SAVE_ERROR_ACTION_TABLE[E.SAVE_FAILED];
    return { category, description, action, error };
}

function findErrorCategory(table: ErrorCategoryMap, code?: number | string): E | undefined {
    if (code === undefined) return;
    for (const [key, codes] of table.entries()) {
        if (codes.includes(code)) {
            return key as E;
        }
    }
}

// @note MobileError 跟 DesktopError 这俩老大哥复用了相同的错误码，含义还不一样，这里根据环境做一下兼容

/** 移动端错误 */
const M = (e: MobileErrorCode) => (ENV.IS_MOBILE_LOCAL_EDIT ? e : -1);
/** 桌面端错误 */
const D = (e: DesktopErrorCode) => (ENV.IS_PC_LOCAL_EDIT ? e : -1);


const enum E {
    None,
    /** 文档打开失败 */
    LOAD_FAILED,
    /** 文档保存失败 */
    SAVE_FAILED,
    /** 应用版本过低，请更新后重试 */
    APP_VERSION_TOO_LOW,
    /** 系统版本过低，请升级系统后重试 */
    SYSTEM_VERSION_TOO_LOW,
    /** 磁盘空间不足，请清理后重试 */
    DISK_FULL,
    /** 暂不支持打开加密文档 */
    ENCRYPTED_FILE,
    /** 导出任务已取消，请重试 */
    EXPORT_CANCELLED,
    /** 文档导出失败 */
    EXPORT_FAILED,
    /** 文档打开失败，无法编辑 */
    LOAD_FAILED_READONLY,
    /** 文档打开失败，请重试 */
    LOAD_FAILED_RETRY,
    /** 文件已损坏，无法打开 */
    FILE_CORRUPTED,
    /** 文件已损坏，无法保存 */
    FILE_CORRUPTED_SAVE,
    /** 文档被其他应用使用中，暂时无法打开 */
    FILE_IN_USE,
    /** 文档被其他应用使用中，请先关闭其他应用，或另存文档 */
    FILE_IN_USE_SAVE,
    /** 文档打开失败，请检查文档存储路径是否发生改变 */
    FILE_PATH_CHANGED,
    /** 程序初始化失败，请重启应用重试 */
    INIT_FAILED,
    /** 网络异常，请在网络恢复后重试 */
    NETWORK_ERROR,
    /** 暂无权限读取文档，打开失败 */
    NO_PERMISSION,
    /** 暂无权限保存文档，请重试或另存文档 */
    NO_PERMISSION_SAVE,
    /** 文档打开失败，密码错误 */
    PASSWORD_INCORRECT,
    /** 文档打开失败，请输入密码 */
    PASSWORD_REQUIRED,
    /** 文档保存失败，请重试或另存文档 */
    SAVE_FAILED_RETRY,
    /** 文档保存失败，请检查文档存储路径是否发生改变 */
    SAVE_PATH_CHANGED,
    /** 保存路径不存在，请选择新的保存位置 */
    SAVE_PATH_NOT_EXIST,
}

export enum ErrorNoticeAction {
    Upgrade = 1,
    CloseTab,
    SaveAs,
    Retry,
    /**
     * 移动端本地编辑新增tbs兜底仅查看
     */
    ReadOnly,
    Feedback,
}

export const ERROR_MESSAGE_TABLE: Record<E, string> = {
    [E.None]: '',
    [E.LOAD_FAILED]: i18n.t('文档打开失败'),
    [E.SAVE_FAILED]: i18n.t('文档保存失败'),
    [E.APP_VERSION_TOO_LOW]: i18n.t('应用版本过低，请更新后重试'),
    [E.SYSTEM_VERSION_TOO_LOW]: i18n.t('系统版本过低，请升级系统后重试'),
    [E.DISK_FULL]: i18n.t('磁盘空间不足，请清理后重试'),
    [E.ENCRYPTED_FILE]: i18n.t('暂不支持打开加密文档'),
    [E.EXPORT_CANCELLED]: i18n.t('导出任务已取消，请重试'),
    [E.EXPORT_FAILED]: i18n.t('文档导出失败'),
    [E.LOAD_FAILED_READONLY]: i18n.t('文档打开失败，无法编辑'),
    [E.LOAD_FAILED_RETRY]: i18n.t('文档打开失败，请重试'),
    [E.FILE_CORRUPTED]: i18n.t('文件已损坏，无法打开'),
    [E.FILE_CORRUPTED_SAVE]: i18n.t('文件已损坏，无法保存'),
    [E.FILE_IN_USE]: i18n.t('文档被其他应用使用中，暂时无法打开'),
    [E.FILE_IN_USE_SAVE]: i18n.t('文档被其他应用使用中，请先关闭其他应用，或另存文档'),
    [E.FILE_PATH_CHANGED]: i18n.t('文档打开失败，请检查文档存储路径是否发生改变'),
    [E.INIT_FAILED]: i18n.t('程序初始化失败，请重启应用重试'),
    [E.NETWORK_ERROR]: i18n.t('网络异常，请在网络恢复后重试'),
    [E.NO_PERMISSION]: i18n.t('暂无权限读取文档，打开失败'),
    [E.NO_PERMISSION_SAVE]: i18n.t('暂无权限保存文档，请重试或另存文档'),
    [E.PASSWORD_INCORRECT]: i18n.t('文档打开失败，密码错误'),
    [E.PASSWORD_REQUIRED]: i18n.t('文档打开失败，请输入密码'),
    [E.SAVE_FAILED_RETRY]: i18n.t('文档保存失败，请重试或另存文档'),
    [E.SAVE_PATH_CHANGED]: i18n.t('文档保存失败，请检查文档存储路径是否发生改变'),
    [E.SAVE_PATH_NOT_EXIST]: i18n.t('保存路径不存在，请选择新的保存位置'),
};

type ErrorCategoryMap = Map<E, readonly (string| number)[]>;

/**
 * 打开文档错误表
 */
const LOAD_ERROR_CATEGORY_MAP: ErrorCategoryMap = new Map([
    /** 应用版本过低，请更新后重试 */
    [E.APP_VERSION_TOO_LOW, [
        ConvertErrorType.UnsupportedAppEnvironmentError,
    ]],
    /** 系统版本过低，请升级系统后重试 */
    [E.SYSTEM_VERSION_TOO_LOW, [
        ConvertErrorType.UnsupportedSystemEnvironmentError,
    ]],
    /** 磁盘空间不足，请清理后重试 */
    [E.DISK_FULL, [
        ConvertErrorType.OutOfSpaceError,
    ]],
    /** 暂不支持打开加密文档 */
    [E.ENCRYPTED_FILE, [
        ConvertErrorType.DocumentEncryptedError,
    ]],
    /** 文档打开失败 */
    [E.LOAD_FAILED, [
        ImporterErrorCode.kUnknownDocsType,
        ImporterErrorCode.kInvalidFilesystem,
        ImporterErrorCode.kFilesystemEmitError,
        ImporterErrorCode.kTreewalkerEmitError,
        ImporterErrorCode.kInvalidRefResolver,
        ImporterErrorCode.kInvalidPackageContext,
        ImporterErrorCode.kInvalidPartContext,
        ImporterErrorCode.kInvalidPackageDescriptor,
        ImporterErrorCode.kInvalidPartDescriptor,
        ImporterErrorCode.kInvalidPackageStrategy,
        ImporterErrorCode.kInvalidPartStrategy,
        ImporterErrorCode.kInvalidAssetPart,
        ImporterErrorCode.kInvalidEncryptionContext,
        ImporterErrorCode.kInvalidImportConfig,
        ImporterErrorCode.kInvalidNamespaceWhenSeekSectpr,
        ImporterErrorCode.kImportCancel,
        ImporterErrorCode.kNotSupported,
        ImporterErrorCode.kInvalidImportCache,

        ConvertErrorType.IOUnavailableError,
        ConvertErrorType.UnexpectedFormatConvertError,
        ConvertErrorType.UnexpectedOutputFormatError,
        ConvertErrorType.UIModelError,
        ConvertErrorType.UnknownError,
        ConvertErrorType.UnexpectedProcessKilledError,
        ConvertErrorType.UnexpectedFormatConvertNoEndError,
        ConvertErrorType.CacheDataIOError,
        ConvertErrorType.MissingConverterError,

        D(DesktopErrorCode.ErrorUnknown),
    ]],
    /** 文档打开失败，无法编辑 */
    [E.LOAD_FAILED_READONLY, [
        M(MobileErrorCode.ErrorUnknown),
        M(MobileErrorCode.DomainError),
        M(MobileErrorCode.RequestParseError),
        M(MobileErrorCode.IOResultParseError),
        M(MobileErrorCode.NotImplemented),
    ]],
    /** 文档打开失败，请重试' */
    [E.LOAD_FAILED_RETRY, [
        M(MobileErrorCode.FileReadFailed),
    ]],
    /** 文档打开失败，请检查文档存储路径是否发生改变 */
    [E.FILE_PATH_CHANGED, [
        ConvertErrorType.IONotFoundError,

        M(MobileErrorCode.FileNoExist),
        D(DesktopErrorCode.ErrorNotEntity),
    ]],
    /** 程序初始化失败，请重启应用重试 */
    [E.INIT_FAILED, [
        D(DesktopErrorCode.ErrorConverterInstall),
    ]],
    /** 文档被其他应用使用中，暂时无法打开 */
    [E.FILE_IN_USE, [
        ConvertErrorType.IOShareViolationError,

        D(DesktopErrorCode.ErrorBusy),
    ]],
    /** 文件已损坏，无法打开 */
    [E.FILE_CORRUPTED, [
        ImporterErrorCode.kInvalidSheetId,
        ConvertErrorType.DocumentBrokenPackageError,
        ConvertErrorType.DocumentBrokenOOXMLError,
    ]],
    /** 暂无权限读取文档，打开失败 */
    [E.NO_PERMISSION, [
        ConvertErrorType.IOUnauthorizedAccessError,
        D(DesktopErrorCode.ErrorPermittion),
    ]],
] as const) ;

/**
 * 保存文档错误表
 */
const SAVE_ERROR_CATEGORY_MAP: ErrorCategoryMap = new Map([
    /** 应用版本过低，请更新后重试 */
    [E.APP_VERSION_TOO_LOW, [
        ConvertErrorType.UnsupportedAppEnvironmentError,
    ]],
    /** 系统版本过低，请升级系统后重试 */
    [E.SYSTEM_VERSION_TOO_LOW, [
        ConvertErrorType.UnsupportedSystemEnvironmentError,
    ]],
    /** 磁盘空间不足，请清理后重试 */
    [E.DISK_FULL, [
        ConvertErrorType.OutOfSpaceError,
    ]],
    /** 导出任务已取消，请重试 */
    [E.EXPORT_CANCELLED, [
        ExporterErrorCode.kExportCancel,
    ]],
    /** 文档导出失败 */
    [E.EXPORT_FAILED, [
        ExporterErrorCode.kUnknownDocsType,
        ExporterErrorCode.kInvalidFileHandle,
        ExporterErrorCode.kRequiredVisitor,
        ExporterErrorCode.kInvalidPartStrategy,
        ExporterErrorCode.kEmptyDataContent,
        ExporterErrorCode.kLostPartVisitor,
        ExporterErrorCode.kExportPBToXFailed,
        ExporterErrorCode.kEmbedFontFileNotExist,
        ExporterErrorCode.kEmbedFontOriginFileOpenError,
        ExporterErrorCode.kEmbedFontDstFileOpenError,
        ExporterErrorCode.kInvalidOOXMLNode,
        ExporterErrorCode.kEncryptFileFailed,
        ExporterErrorCode.kInvalidSheetModel,
        ExporterErrorCode.kInvalidWorksheetId,
        ExporterErrorCode.kInvalidWorksheetChartData,
        ExporterErrorCode.kInvalidPivotTable,
        ExporterErrorCode.kInvalidPivotCacheDefinitions,
        ExporterErrorCode.kInvalidPivotCacheRecords,
        ExporterErrorCode.kInvalidSheetPivotTable,
        ExporterErrorCode.kInvalidSheetSharedStringCache,
        ExporterErrorCode.kInvalidSheetFrozenProperty,
        ExporterErrorCode.kInvalidSheetChart,
        ExporterErrorCode.kInvalidSheetComment,
        ExporterErrorCode.kAllWorksheetAreHidden,
        ExporterErrorCode.kInvalidSlideModel,
        ExporterErrorCode.kEmptySlideViewPr,
        ExporterErrorCode.kEmptyTableStyleList,
        ExporterErrorCode.kEmptyPresPros,
        ExporterErrorCode.kInvalidSlideId,
        ExporterErrorCode.kInvalidSlideMasterId,
        ExporterErrorCode.kInvalidNotesSlideId,
        ExporterErrorCode.kNotesMasterNotFound,
        ExporterErrorCode.kInvalidSlideSection,
        ExporterErrorCode.kSlideTableNotFound,
    ]],
    /** 文档被其他应用使用中，请先关闭其他应用，或另存文档 */
    [E.FILE_IN_USE_SAVE, [
        ConvertErrorType.IOShareViolationError,

        D(DesktopErrorCode.ErrorBusy),
    ]],
    /** 文档保存失败 */
    [E.SAVE_FAILED, [
        ExporterErrorCode.kUnknownDocsType,
        ImporterErrorCode.kInvalidFilesystem,
        ImporterErrorCode.kFilesystemEmitError,
        ImporterErrorCode.kTreewalkerEmitError,
        ImporterErrorCode.kInvalidRefResolver,
        ImporterErrorCode.kInvalidPackageContext,
        ImporterErrorCode.kInvalidPartContext,
        ImporterErrorCode.kInvalidPackageDescriptor,
        ImporterErrorCode.kInvalidPartDescriptor,
        ImporterErrorCode.kInvalidPackageStrategy,
        ImporterErrorCode.kInvalidPartStrategy,
        ImporterErrorCode.kInvalidAssetPart,
        ImporterErrorCode.kInvalidEncryptionContext,
        ImporterErrorCode.kInvalidImportConfig,
        ImporterErrorCode.kInvalidNamespaceWhenSeekSectpr,
        ImporterErrorCode.kImportCancel,
        ImporterErrorCode.kNotSupported,
        ImporterErrorCode.kInvalidImportCache,
        ImporterErrorCode.kLostRequiredData,

        ConvertErrorType.UnexpectedInputIOError,
        ConvertErrorType.UnexpectedInputFormatError,
        ConvertErrorType.ComponentExecuteError,
        ConvertErrorType.UnexpectedFormatConvertError,
        ConvertErrorType.UnknownError,
        ConvertErrorType.UnexpectedProcessKilledError,
        ConvertErrorType.UnexpectedFormatConvertNoEndError,
        ConvertErrorType.MissingConverterError,
        ConvertErrorType.ComponentFetchError,
        ConvertErrorType.IOUnavailableError,

        M(MobileErrorCode.ErrorUnknown),
        M(MobileErrorCode.DomainError),
        M(MobileErrorCode.RequestParseError),
        M(MobileErrorCode.IOResultParseError),

        D(DesktopErrorCode.ErrorUnknown),
        D(DesktopErrorCode.ErrorConverterInstall),
    ]],
    /** 文档保存失败，请重试或另存文档 */
    [E.SAVE_FAILED_RETRY, [
        M(MobileErrorCode.NotImplemented),
    ]],
    /** 文档保存失败，请检查文档存储路径是否发生改变 */
    [E.SAVE_PATH_CHANGED, [
        ImporterErrorCode.kFileNotExist,
    ]],
    /** 保存路径不存在，请选择新的保存位置 */
    [E.SAVE_PATH_NOT_EXIST, [
        ConvertErrorType.IONotFoundError,
        M(MobileErrorCode.FileNoExist),
        D(DesktopErrorCode.ErrorNotEntity),
    ]],
    /** 暂无权限保存文档，请重试或另存文档 */
    [E.NO_PERMISSION_SAVE, [
        D(DesktopErrorCode.ErrorPermittion),
        ConvertErrorType.IOUnauthorizedAccessError,
    ]],
] as const);

const LOAD_ERROR_ACTION_TABLE = {
    [E.APP_VERSION_TOO_LOW]: ErrorNoticeAction.Upgrade,
    [E.SYSTEM_VERSION_TOO_LOW]: ErrorNoticeAction.Upgrade,
    [E.LOAD_FAILED]: ErrorNoticeAction.CloseTab,
    [E.LOAD_FAILED_READONLY]: ErrorNoticeAction.ReadOnly,
} as const;

const SAVE_ERROR_ACTION_TABLE = {
    [E.APP_VERSION_TOO_LOW]: ErrorNoticeAction.Upgrade,
    [E.SYSTEM_VERSION_TOO_LOW]: ErrorNoticeAction.Upgrade,
    [E.SAVE_FAILED]: ErrorNoticeAction.Feedback,
    [E.SAVE_FAILED_RETRY]: ErrorNoticeAction.Retry,
    [E.FILE_IN_USE_SAVE]: ErrorNoticeAction.SaveAs,
    [E.NO_PERMISSION_SAVE]: ErrorNoticeAction.SaveAs,
    [E.SAVE_PATH_NOT_EXIST]: ErrorNoticeAction.SaveAs,
    [E.SAVE_PATH_CHANGED]: ErrorNoticeAction.SaveAs,
} as const;
